import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Link2, Plus, Trash2, X, Loader2, Wand2, Activity, AlertTriangle, ArrowRight, ArrowLeftRight, CheckCircle2, Info } from "lucide-react";
import { cn } from "@/lib/utils";

export const CARDINALITY_OPTIONS = [
  { value: "one_to_one", label: "One to one (1:1)", glyph: ["1", "1"] },
  { value: "one_to_many", label: "One to many (1:*)", glyph: ["1", "*"] },
  { value: "many_to_one", label: "Many to one (*:1)", glyph: ["*", "1"] },
  { value: "many_to_many", label: "Many to many (*:*)", glyph: ["*", "*"] },
];

const NUM = new Set(["number", "integer", "float", "currency", "percent"]);
const DATE = new Set(["date", "datetime"]);
export const normType = (t) => { t = (t || "string").toLowerCase(); return NUM.has(t) ? "number" : DATE.has(t) ? "date" : ["string", "text", "category"].includes(t) ? "text" : t; };
const fieldTypes = (ds) => Object.fromEntries([...(ds?.schema || []), ...(ds?.calc_fields || []).map((f) => ({ name: f.name, type: f.type || "number" }))].map((c) => [c.name, c.type]));

// Which side filters the other (single direction): the "one" side; M:M → the To dataset.
export function flowText(cardinality, direction, leftName, rightName) {
  if (direction === "both") return `Filters flow both ways between ${leftName} and ${rightName}`;
  if (cardinality === "one_to_one") return `Filters flow both ways (1:1)`;
  const src = cardinality === "one_to_many" ? leftName : rightName;
  const dst = src === leftName ? rightName : leftName;
  return `Filters flow from ${src} to ${dst}`;
}

function ProfilePanel({ profile, onApply, cardinality }) {
  if (!profile) return null;
  const Side = ({ s, label }) => (
    <div className="rounded-lg bg-[color:var(--surface-2)] p-2 text-[11px] space-y-0.5">
      <div className="font-medium text-[color:var(--ink-2)] truncate">{label}</div>
      <div className="flex justify-between"><span className="text-[color:var(--ink-3)]">Rows</span><span>{s.rows.toLocaleString()}</span></div>
      <div className="flex justify-between"><span className="text-[color:var(--ink-3)]">Distinct</span><span>{s.distinct.toLocaleString()} {s.unique ? <span className="text-[color:var(--success)]">· unique</span> : <span className="text-[#B45309]">· repeats</span>}</span></div>
      <div className="flex justify-between"><span className="text-[color:var(--ink-3)]">Nulls</span><span>{s.nulls}</span></div>
      <div className="flex justify-between"><span className="text-[color:var(--ink-3)]">Match rate</span><span className={s.match_rate < 0.5 ? "text-[color:var(--danger)]" : ""}>{Math.round(s.match_rate * 100)}%</span></div>
      {s.unmatched_sample?.length > 0 && <div className="text-[color:var(--ink-3)] truncate" title={s.unmatched_sample.join(", ")}>Unmatched: {s.unmatched_sample.join(", ")}</div>}
    </div>
  );
  const sugg = CARDINALITY_OPTIONS.find((o) => o.value === profile.suggested_cardinality);
  return (
    <div data-testid="rel-profile-panel" className="space-y-2">
      <div className="grid grid-cols-2 gap-2"><Side s={profile.left} label={profile.left.dataset} /><Side s={profile.right} label={profile.right.dataset} /></div>
      <div className="flex items-center gap-2 text-[11px]">
        <span className="text-[color:var(--ink-3)]">Data supports:</span>
        <span data-testid="rel-profile-suggested" className="font-medium text-[color:var(--ink)]">{sugg?.label}</span>
        {profile.suggested_cardinality !== cardinality && <Button data-testid="rel-profile-apply" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={() => onApply(profile.suggested_cardinality)}>Apply</Button>}
        {profile.sampled && <span className="text-[color:var(--ink-3)]">(sampled)</span>}
      </div>
      {profile.notes?.map((n, i) => <p key={i} className="text-[11px] text-[#B45309] flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{n}</p>)}
    </div>
  );
}

function ImpactPanel({ impact }) {
  if (!impact) return null;
  if (!impact.total) return <p data-testid="rel-impact-none" className="text-[11px] text-[color:var(--ink-3)] flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-[color:var(--success)]" />No widgets depend on this relationship yet.</p>;
  return (
    <div data-testid="rel-impact-panel" className="rounded-lg border border-[#F59E0B44] bg-[#F59E0B0d] p-2 text-[11px] space-y-1">
      <p className="text-[#B45309] font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{impact.total} widget{impact.total === 1 ? "" : "s"} across {impact.dashboards.length} dashboard{impact.dashboards.length === 1 ? "" : "s"} use this relationship</p>
      <ul className="space-y-0.5 text-[color:var(--ink-2)]">
        {impact.dashboards.map((d) => <li key={d.id}><a href={`/builder/${d.id}`} className="hover:underline text-[color:var(--brand-ocean)]">{d.name}</a> · {d.widgets.map((w) => w.title).join(", ")}</li>)}
      </ul>
      {impact.disconnects && <p className="text-[color:var(--ink-3)]">Removing it leaves these datasets with no other path.</p>}
    </div>
  );
}

export function RelationshipDialog({ open, onOpenChange, datasets, contract, onSaved, initial = null }) {
  const editing = !!contract;
  const [name, setName] = useState("");
  const [leftId, setLeftId] = useState("");
  const [rightId, setRightId] = useState("");
  const [keys, setKeys] = useState([{ left_field: "", right_field: "" }]);
  const [cardinality, setCardinality] = useState("many_to_one");
  const [direction, setDirection] = useState("single");
  const [active, setActive] = useState(true);
  const [confirmM2M, setConfirmM2M] = useState(false);
  const [allowMismatch, setAllowMismatch] = useState(false);
  const [busy, setBusy] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [profiling, setProfiling] = useState(false);
  const [profile, setProfile] = useState(null);
  const [impact, setImpact] = useState(null);
  const [inUse, setInUse] = useState(null);

  useEffect(() => {
    if (!open) return;
    setProfile(null); setImpact(null); setInUse(null); setConfirmM2M(false); setAllowMismatch(false);
    const src = contract || initial;
    if (src) {
      setName(src.name || "");
      setLeftId(src.left_dataset_id); setRightId(src.right_dataset_id);
      setKeys(src.keys?.length ? src.keys.map((k) => ({ ...k })) : [{ left_field: "", right_field: "" }]);
      setCardinality(src.cardinality || "many_to_one");
      setDirection(src.cross_filter_direction || "single");
      setActive(src.active !== false);
      if (contract) setConfirmM2M(contract.cardinality === "many_to_many");
    } else {
      setName(""); setLeftId(""); setRightId("");
      setKeys([{ left_field: "", right_field: "" }]);
      setCardinality("many_to_one"); setDirection("single"); setActive(true);
    }
    if (contract) api.get(`/join-contracts/${contract.id}/impact`).then(({ data }) => setImpact(data)).catch(() => {});
  }, [open, contract, initial]);

  const leftDs = datasets.find((d) => d.id === leftId);
  const rightDs = datasets.find((d) => d.id === rightId);
  const lt = useMemo(() => fieldTypes(leftDs), [leftDs]);
  const rt = useMemo(() => fieldTypes(rightDs), [rightDs]);
  const leftFields = Object.keys(lt);
  const rightFields = Object.keys(rt);
  const validKeys = keys.filter((k) => k.left_field && k.right_field);
  const mismatches = validKeys.filter((k) => normType(lt[k.left_field]) !== normType(rt[k.right_field]));
  const changedKeys = editing && JSON.stringify(validKeys.map((k) => [k.left_field, k.right_field])) !== JSON.stringify((contract?.keys || []).map((k) => [k.left_field, k.right_field]));

  const updKey = (i, k, v) => { setKeys((ks) => ks.map((x, j) => j === i ? { ...x, [k]: v } : x)); setProfile(null); };

  const autodetect = async () => {
    if (!leftId || !rightId) { toast.info("Pick both datasets first"); return; }
    setDetecting(true);
    try {
      const { data } = await api.post("/join-contracts/autodetect", { left_dataset_id: leftId, right_dataset_id: rightId });
      if (!data.suggestions?.length) { toast.info("No matching key columns found — map them manually"); return; }
      setKeys(data.suggestions.slice(0, 3).map((s) => ({ left_field: s.left_field, right_field: s.right_field })));
      if (!name.trim()) setName(`${leftDs?.name} → ${rightDs?.name}`);
      toast.success(`${data.suggestions.length} key suggestion(s) applied`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Auto-detect failed"); }
    finally { setDetecting(false); }
  };

  const runProfile = async () => {
    if (!validKeys.length) { toast.info("Pick at least one key pair"); return; }
    setProfiling(true);
    try { const { data } = await api.post("/join-contracts/profile-keys", { left_dataset_id: leftId, right_dataset_id: rightId, keys: validKeys }); setProfile(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Profiling failed"); }
    finally { setProfiling(false); }
  };

  const detailMsg = (d) => (typeof d === "string" ? d : d?.message || "Save failed");

  const save = async () => {
    if (!name.trim() || !leftId || !rightId || !validKeys.length) { toast.error("Name, both datasets and at least one key pair are required"); return; }
    if (cardinality === "many_to_many" && !confirmM2M) { toast.error("Confirm the many-to-many fan-out to continue"); return; }
    if (mismatches.length && !allowMismatch) { toast.error("Key types don't match — confirm to compare them as text"); return; }
    if (validKeys.some((k) => !lt[k.left_field] || !rt[k.right_field])) { toast.error("A key field no longer exists — pick a replacement before saving"); return; }
    setBusy(true);
    try {
      const body = { name: name.trim(), keys: validKeys, cardinality, cross_filter_direction: direction, active, confirm_many_to_many: confirmM2M, allow_type_mismatch: allowMismatch };
      const { data } = editing
        ? await api.patch(`/join-contracts/${contract.id}`, body)
        : await api.post("/join-contracts", { ...body, left_dataset_id: leftId, right_dataset_id: rightId });
      toast.success(editing ? "Relationship updated" : "Relationship created");
      (data.warnings || []).forEach((w) => toast.warning(w, { duration: 7000 }));
      if (editing && data.impact?.total) toast.info(`${data.impact.total} widget(s) across ${data.impact.dashboards?.length || 0} dashboard(s) will re-query with the new relationship`, { duration: 6000 });
      onSaved();
    } catch (e) { toast.error(detailMsg(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (force = false) => {
    setBusy(true);
    try { await api.delete(`/join-contracts/${contract.id}${force ? "?force=true" : ""}`); toast.success("Relationship deleted"); onSaved(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "in_use") setInUse(d.impact);
      else toast.error(detailMsg(d));
    }
    finally { setBusy(false); }
  };

  const Glyph = CARDINALITY_OPTIONS.find((o) => o.value === cardinality) || CARDINALITY_OPTIONS[2];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="rel-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Link2 className="w-5 h-5 text-[color:var(--brand-ocean)]" />{editing ? "Edit relationship" : "New relationship"}</DialogTitle>
          <DialogDescription>Relationships let one visual combine fields from several datasets through the query engine.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1 max-h-[62vh] overflow-auto pr-1">
          <div><Label>Name</Label><Input data-testid="rel-name" className="mt-1.5 bg-[color:var(--surface)]" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Orders → Customers" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">From dataset <span className="text-[color:var(--ink-3)] font-mono">{Glyph.glyph[0]}</span></Label>
              <Select value={leftId} onValueChange={(v) => { setLeftId(v); setProfile(null); }} disabled={editing}>
                <SelectTrigger data-testid="rel-left-ds" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent>{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">To dataset <span className="text-[color:var(--ink-3)] font-mono">{Glyph.glyph[1]}</span></Label>
              <Select value={rightId} onValueChange={(v) => { setRightId(v); setProfile(null); }} disabled={editing}>
                <SelectTrigger data-testid="rel-right-ds" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent>{datasets.filter((d) => d.id !== leftId).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {leftId && rightId && (
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-[color:var(--ink-3)]">Join keys</Label>
                <div className="flex items-center gap-1">
                  <Button data-testid="rel-autodetect" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-mint)]" onClick={autodetect} disabled={detecting}>
                    {detecting ? <Loader2 className="w-3 h-3 mr-0.5 animate-spin" /> : <Wand2 className="w-3 h-3 mr-0.5" />}Auto detect
                  </Button>
                  <Button data-testid="rel-profile" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={runProfile} disabled={profiling || !validKeys.length}>
                    {profiling ? <Loader2 className="w-3 h-3 mr-0.5 animate-spin" /> : <Activity className="w-3 h-3 mr-0.5" />}Profile keys
                  </Button>
                  <Button data-testid="rel-add-key" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={() => setKeys((ks) => [...ks, { left_field: "", right_field: "" }])}><Plus className="w-3 h-3 mr-0.5" />Key pair</Button>
                </div>
              </div>
              <div className="space-y-2 mt-1.5">
                {keys.map((k, i) => {
                  const bad = k.left_field && k.right_field && normType(lt[k.left_field]) !== normType(rt[k.right_field]);
                  return (
                    <div key={i}>
                      <div className="flex gap-1.5 items-center">
                        <Select value={k.left_field} onValueChange={(v) => updKey(i, "left_field", v)}>
                          <SelectTrigger data-testid={`relkey-left-${i}`} data-missing={k.left_field && !lt[k.left_field] ? "true" : undefined} className={cn("bg-[color:var(--surface)] flex-1 min-w-0 h-8 text-xs", bad && "border-[#F59E0B]", k.left_field && !lt[k.left_field] && "border-[color:var(--danger)] text-[color:var(--danger)]")}><SelectValue placeholder={`${leftDs?.name} field`} /></SelectTrigger>
                          <SelectContent>
                            {k.left_field && !lt[k.left_field] && <SelectItem value={k.left_field} disabled>{k.left_field} <span className="text-[color:var(--danger)]">· missing</span></SelectItem>}
                            {leftFields.map((f) => <SelectItem key={f} value={f}>{f} <span className="text-[color:var(--ink-3)]">· {normType(lt[f])}</span></SelectItem>)}
                          </SelectContent>
                        </Select>
                        <span className="text-[color:var(--ink-3)] text-xs shrink-0">=</span>
                        <Select value={k.right_field} onValueChange={(v) => updKey(i, "right_field", v)}>
                          <SelectTrigger data-testid={`relkey-right-${i}`} data-missing={k.right_field && !rt[k.right_field] ? "true" : undefined} className={cn("bg-[color:var(--surface)] flex-1 min-w-0 h-8 text-xs", bad && "border-[#F59E0B]", k.right_field && !rt[k.right_field] && "border-[color:var(--danger)] text-[color:var(--danger)]")}><SelectValue placeholder={`${rightDs?.name} field`} /></SelectTrigger>
                          <SelectContent>
                            {k.right_field && !rt[k.right_field] && <SelectItem value={k.right_field} disabled>{k.right_field} <span className="text-[color:var(--danger)]">· missing</span></SelectItem>}
                            {rightFields.map((f) => <SelectItem key={f} value={f}>{f} <span className="text-[color:var(--ink-3)]">· {normType(rt[f])}</span></SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-[color:var(--ink-3)]" onClick={() => setKeys((ks) => ks.filter((_, j) => j !== i))}><X className="w-3.5 h-3.5" /></Button>
                      </div>
                      {bad && <p data-testid={`relkey-mismatch-${i}`} className="text-[11px] text-[#B45309] mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{k.left_field} is {normType(lt[k.left_field])}, {k.right_field} is {normType(rt[k.right_field])}</p>}
                    </div>
                  );
                })}
              </div>
              {mismatches.length > 0 && (
                <label className="mt-2 flex items-center gap-2 text-[11px] text-[color:var(--ink-2)]"><Checkbox data-testid="rel-allow-mismatch" checked={allowMismatch} onCheckedChange={(v) => setAllowMismatch(!!v)} />Compare mismatched keys as text anyway</label>
              )}
              <div className="mt-2"><ProfilePanel profile={profile} cardinality={cardinality} onApply={(c) => { setCardinality(c); if (c !== "many_to_many") setConfirmM2M(false); }} /></div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Cardinality</Label>
              <Select value={cardinality} onValueChange={(v) => { setCardinality(v); if (v !== "many_to_many") setConfirmM2M(false); }}>
                <SelectTrigger data-testid="rel-cardinality" className="mt-1.5 bg-[color:var(--surface)] h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{CARDINALITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Cross-filter direction</Label>
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger data-testid="rel-direction" className="mt-1.5 bg-[color:var(--surface)] h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single"><span className="inline-flex items-center gap-1"><ArrowRight className="w-3 h-3" />Single</span></SelectItem>
                  <SelectItem value="both"><span className="inline-flex items-center gap-1"><ArrowLeftRight className="w-3 h-3" />Both</span></SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {leftDs && rightDs && <p data-testid="rel-flow-text" className="text-[11px] text-[color:var(--ink-3)] flex items-center gap-1"><Info className="w-3 h-3 shrink-0" />{flowText(cardinality, direction, leftDs.name, rightDs.name)}. Filters that can't flow are ignored with a warning on the visual.</p>}
          {cardinality === "many_to_many" && (
            <label data-testid="rel-m2m-confirm-row" className="flex items-start gap-2 rounded-lg border border-[#F59E0B44] bg-[#F59E0B0d] px-3 py-2 text-[11px] text-[color:var(--ink-2)]">
              <Checkbox data-testid="rel-m2m-confirm" checked={confirmM2M} onCheckedChange={(v) => setConfirmM2M(!!v)} className="mt-0.5" />
              <span><b className="text-[#B45309]">Many-to-many</b> joins multiply matching rows — totals across this relationship can be inflated. I understand and want to keep it.</span>
            </label>
          )}
          <div className="flex items-center justify-between rounded-lg bg-[color:var(--surface-2)] px-3 py-2">
            <div>
              <Label className="text-xs text-[color:var(--ink-2)]">Active</Label>
              <p className="text-[10px] text-[color:var(--ink-3)]">Inactive relationships are kept but not used by queries.</p>
            </div>
            <Switch data-testid="rel-active" checked={active} onCheckedChange={setActive} />
          </div>
          {editing && (contract?.broken_keys || []).length > 0 && (
            <div data-testid="rel-broken-banner" className="rounded-lg border border-[color:var(--danger)]/50 bg-[color:rgba(220,38,38,0.06)] p-2 text-[11px] space-y-0.5">
              <p className="text-[color:var(--danger)] font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" />This relationship is broken — the schema changed</p>
              {contract.broken_keys.map((b, i) => (
                <p key={i} className="text-[color:var(--ink-2)]">{b.field ? <>Field <span className="font-mono">{b.field}</span> no longer exists in {b.dataset || "the dataset"} — pick a replacement key above.</> : "One of the datasets was deleted or is no longer accessible."}</p>
              ))}
              <p className="text-[color:var(--ink-3)]">Widgets that span it show “Relationship broken” until it is fixed.</p>
            </div>
          )}
          {editing && <ImpactPanel impact={impact} />}
          {editing && impact?.total > 0 && (changedKeys || cardinality !== (contract?.cardinality || "many_to_one") || (!active && contract?.active !== false)) && (
            <p data-testid="rel-change-impact" className="text-[11px] text-[#B45309] flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
              Saving this change alters the results of {impact.total} widget{impact.total === 1 ? "" : "s"} listed above{!active && contract?.active !== false ? " — deactivating stops their cross-dataset data" : ""}.</p>
          )}
          {inUse && (
            <div data-testid="rel-delete-blocked" className="rounded-lg border border-[color:var(--danger)] p-2 text-[11px] space-y-1">
              <p className="text-[color:var(--danger)] font-medium">{inUse.total} widget(s) rely on this relationship and will lose their cross-dataset data.</p>
              <Button data-testid="rel-delete-force" size="sm" variant="destructive" className="h-7" disabled={busy} onClick={() => del(true)}>Delete anyway</Button>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          {editing && <Button data-testid="rel-delete" variant="outline" onClick={() => del(false)} disabled={busy} className="text-[color:var(--danger)] mr-auto"><Trash2 className="w-4 h-4 mr-1" />Delete</Button>}
          <Button data-testid="rel-save" onClick={save} disabled={busy} className="bg-[color:var(--brand-ocean)] text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Link2 className="w-4 h-4 mr-1" />}{editing ? "Save changes" : "Create relationship"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
